// src/hooks/useZoom.js - Hook for accessing Zoom Integration data from ZoomStore
import { useMemo } from 'react';
import useZoomStore from '../stores/ZoomStore';
import { useSocket } from '../contexts/SocketContext';

/**
 * Custom hook for accessing Zoom integration data and operations
 *
 * Provides access to:
 * - Zoom account connection status
 * - OAuth flow for connecting Zoom
 * - Meeting creation capabilities
 *
 * Uses Zustand ZoomStore with individual selectors for optimal React re-rendering.
 * Store initialization is handled at app level in SocketContext.
 *
 * @returns {object} Zoom data, operations, and computed values
 */
export const useZoom = () => {
  const socket = useSocket();

  // 🚨 Use individual selectors instead of destructuring entire store
  // This ensures React re-renders when specific properties change

  // Connection status
  const zoomConnection = useZoomStore(state => state.zoomConnection);
  const zoomConnectionLoading = useZoomStore(state => state.zoomConnectionLoading);

  // OAuth flow
  const oauthUrl = useZoomStore(state => state.oauthUrl);
  const oauthInitiating = useZoomStore(state => state.oauthInitiating);

  // Disconnection
  const disconnecting = useZoomStore(state => state.disconnecting);

  // Connection state
  const isConnected = useZoomStore(state => state.isConnected);

  // ================================
  // OPERATIONS (functions don't change, so we can get them once)
  // ================================

  const loadZoomConnectionStatus = useZoomStore(state => state.loadZoomConnectionStatus);
  const startZoomOAuth = useZoomStore(state => state.startZoomOAuth);
  const disconnectZoom = useZoomStore(state => state.disconnectZoom);

  // Utility functions
  const isZoomConnected = useZoomStore(state => state.isZoomConnected);
  const getAccountEmail = useZoomStore(state => state.getAccountEmail);
  const isTokenExpired = useZoomStore(state => state.isTokenExpired);
  const getConnectionStatus = useZoomStore(state => state.getConnectionStatus);

  // Internal initialization state (for fallback check)
  const _initialized = useZoomStore(state => state._initialized);

  // ================================
  // COMPUTED VALUES (memoized)
  // ================================

  /**
   * Whether user is currently connected to Zoom
   */
  const connected = useMemo(() => {
    return isZoomConnected();
  }, [isZoomConnected]);

  /**
   * Whether token has expired
   */
  const tokenExpired = useMemo(() => {
    return isTokenExpired();
  }, [isTokenExpired]);

  /**
   * Get connected account email
   */
  const accountEmail = useMemo(() => {
    return getAccountEmail();
  }, [getAccountEmail]);

  /**
   * Get current connection status string
   */
  const currentStatus = useMemo(() => {
    return getConnectionStatus();
  }, [getConnectionStatus]);

  /**
   * Get user-friendly status message
   */
  const statusMessage = useMemo(() => {
    const messages = {
      disconnected: 'Not connected to Zoom',
      connected: 'Connected to Zoom',
      token_expired: 'Connection expired - please reconnect'
    };
    return messages[currentStatus] || 'Unknown status';
  }, [currentStatus]);

  /**
   * Check if setup requires action (for OAuth flow)
   */
  const requiresSetup = useMemo(() => {
    return !connected || tokenExpired;
  }, [connected, tokenExpired]);

  /**
   * Check if any operation is loading
   */
  const anyLoading = useMemo(() => {
    return zoomConnectionLoading ||
           oauthInitiating ||
           disconnecting;
  }, [zoomConnectionLoading, oauthInitiating, disconnecting]);

  // ================================
  // FALLBACK INITIALIZATION
  // ================================

  // Fallback initialization check (safety net)
  // If app-level initialization failed but we're authenticated, try fallback
  if (socket.isAuthenticated && !socket.storesInitialized && !_initialized) {
    console.warn('⚠️ useZoom: Store not initialized at app level, trying fallback');
    socket.initializeStore('ZoomStore');
  }

  // ================================
  // RETURN INTERFACE
  // ================================

  return {
    // Connection status
    zoomConnection,
    zoomConnectionLoading,
    isConnected,
    connected,
    currentStatus,
    statusMessage,
    tokenExpired,
    requiresSetup,

    // OAuth flow
    oauthUrl,
    oauthInitiating,

    // Account details
    accountEmail,

    // Disconnection
    disconnecting,

    // Computed values
    anyLoading,

    // Operations
    loadZoomConnectionStatus,
    startZoomOAuth,
    disconnectZoom,

    // Legacy/convenience aliases
    loading: zoomConnectionLoading
  };
};

export default useZoom;
